import type { VolumePricingTier } from '../../../types/offers';

/**
 * Resolves the unit price for a quantity from a set of volume bands.
 *
 * Lives in `src/shared/` so the client's order selectors and any server-side revalidation resolve
 * the rate through the same function — a client and server that disagree here would show the
 * customer one price and charge another.
 *
 * Today only the client calls this: the displayed total is presentational, since `addIpsOffer` sends
 * `offerCode` + `quantity` and no amount, leaving IPS/CPQ to price the transaction. Offers publish
 * their bands on server details too, so the server can start deriving the figure itself — the first
 * thing worth moving is the confirmation email's amount, which currently trusts the client's
 * `totalCost`. See "Volume-based pricing is calculated client-side" in
 * docs/plans/tech-debt-tracker.md.
 *
 * Returns `null` when the quantity falls outside every band, which is deliberate rather than a
 * fallback to the nearest rate: the bands define what is purchasable online, so anything beyond
 * them is an assisted-sales conversation, not a cheaper or costlier tier.
 */
export const getUnitPriceForQuantity = (
  tiers: ReadonlyArray<VolumePricingTier> | undefined,
  quantity: number,
): number | null =>
  tiers?.find(({ minQuantity, maxQuantity }) => quantity >= minQuantity && quantity <= maxQuantity)?.unitPrice ?? null;
